























































































































































































import { defineComponent, computed, ref, watch } from '@vue/composition-api';
import { UserMetrics, UserMetricsGranularity, UserMetricsPeriod, UserMetricsTimeline } from '@/types';
import { useClient } from '@/use';
import { userMetricsMenu } from '../state';
import UserMetricsTrendChart from './UserMetricsTrendChart.vue';

const jobsDataTableHeaders = [
  { text: 'User', value: 'user', width: '68%' },
  { text: 'Jobs submitted', value: 'jobs', align: 'end', width: '32%' },
];
const framesDataTableHeaders = [
  { text: 'User', value: 'user', width: '68%' },
  { text: 'Frames evaluated', value: 'frames', align: 'end', width: '32%' },
];
const videosDataTableHeaders = [
  { text: 'User', value: 'user', width: '50%' },
  { text: 'Videos', value: 'videos', align: 'end', width: '20%' },
  { text: 'Uploaded storage', value: 'storage', align: 'end', width: '30%' },
];

const metricsPeriodItems = [
  { text: 'Month to date', value: 'mtd' },
  { text: 'Year to date', value: 'ytd' },
  { text: 'All time', value: 'from_inception' },
];
const timelineGranularityItems = [
  { text: 'Daily', value: 'day' },
  { text: 'Monthly', value: 'month' },
  { text: 'Yearly', value: 'year' },
];

export default defineComponent({
  name: 'UserMetrics',
  components: { UserMetricsTrendChart },
  setup() {
    const tab = ref(0);
    const client = useClient();
    const metricsPeriod = ref<UserMetricsPeriod>('mtd');
    const userMetrics = ref<UserMetrics>();
    const loading = ref(false);
    const loadError = ref('');
    const timeline = ref<UserMetricsTimeline>();
    const timelineLoading = ref(false);
    const timelineError = ref('');
    const timelineGranularity = ref<UserMetricsGranularity>('month');

    const periodLabel = computed(() => {
      const item = metricsPeriodItems.find(({ value }) => value === metricsPeriod.value);
      return item ? item.text : 'All time';
    });

    async function fetchUserMetrics() {
      loading.value = true;
      loadError.value = '';
      try {
        userMetrics.value = await client.userMetrics(metricsPeriod.value);
      } catch (error) {
        userMetrics.value = undefined;
        loadError.value = 'Metrics could not be loaded. Try refreshing in a moment.';
      } finally {
        loading.value = false;
      }
    }

    async function fetchTimeline() {
      timelineLoading.value = true;
      timelineError.value = '';
      try {
        timeline.value = await client.userMetricsTimeline(timelineGranularity.value);
      } catch (error) {
        timeline.value = undefined;
        timelineError.value = 'Trend data could not be loaded. Try refreshing in a moment.';
      } finally {
        timelineLoading.value = false;
      }
    }

    function refreshMetrics() {
      fetchUserMetrics();
      fetchTimeline();
    }

    watch(metricsPeriod, () => {
      if (userMetricsMenu.value) fetchUserMetrics();
    });
    watch(timelineGranularity, () => {
      if (userMetricsMenu.value) fetchTimeline();
    });
    watch(userMetricsMenu, (open) => {
      if (open) refreshMetrics();
    });

    // Defend against an older API container during a rolling deployment as well.
    const timelineSeries = computed(() => (timeline.value?.series || []).slice(-12));
    const jobsPerUser = computed(() => {
      if (!userMetrics.value) {
        return [];
      }

      const obj = userMetrics.value.evaluation_jobs_submitted_per_user;
      return Object.keys(obj).map((user) => ({ user, jobs: obj[user] })).sort((a, b) => b.jobs - a.jobs);
    });

    const framesPerUser = computed(() => {
      if (!userMetrics.value) {
        return [];
      }

      const obj = userMetrics.value.individual_frames_evaluated_per_user;
      return Object.keys(obj).map((user) => ({ user, frames: obj[user] })).sort((a, b) => b.frames - a.frames);
    });

    const videosPerUser = computed(() => {
      if (!userMetrics.value) {
        return [];
      }

      const {
        videos_uploaded_per_user: uploaded,
        server_storage_space_used_by_each_user_in_GB: userStorage,
      } = userMetrics.value;

      return Object.keys(uploaded)
        .map((user) => ({ user, videos: uploaded[user], storage: userStorage[user] || 0 }))
        .sort((a, b) => b.videos - a.videos);
    });

    function formatInteger(value: number) {
      return new Intl.NumberFormat().format(value || 0);
    }

    function formatStorage(value: number) {
      return `${(value || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })} GB`;
    }

    return {
      tab,
      metricsPeriod,
      metricsPeriodItems,
      userMetrics,
      refreshMetrics,
      loading,
      loadError,
      periodLabel,
      formatInteger,
      formatStorage,

      jobsDataTableHeaders,
      jobsPerUser,

      framesDataTableHeaders,
      framesPerUser,

      videosDataTableHeaders,
      videosPerUser,
      timelineGranularity,
      timelineGranularityItems,
      timelineSeries,
      timelineLoading,
      timelineError,
    };
  },
});
